import AuthenticatedLayout from "@/components/AuthenticatedLayout";
import LoadingCard from "@/components/LoadingCard";
import { PropertyHeader } from "@/components/PropertyHeader";
import { RegressionModelDetails } from "@/components/RegressionModelDetails";
import ResponsiveContainer from "@/components/ResponsiveContainer";
import { CompAdjustmentResult, ScatterplotSummary } from "@/components/ScatterPlotSummary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import useFetch from "@/hooks/useFetch";
import { deriveChartStuff } from "@/lib/deriveChartStuff";
import { formatDate, formatPrice } from "@/lib/formatters";
import { normalizeDataBasedOnVersion } from "@/lib/PreparedDataset";
import { PageProps } from "@/types/PageProps";
import RegressionModel from "@/types/RegressionModel";
import { Adjustment } from "@/types/serializers";
import { Head } from "@inertiajs/react";
import { parseCalendarDate } from "@/lib/calendarDate";

type Comp = { id: string };
type Props = PageProps & {
  adjustment: Adjustment;
  dataset_url: string | null;
  regression_model: RegressionModel | null;
  comps: Comp[];
  outliers: string[];
};

function CompAdjustments({
  comps,
  compAdjustmentResults,
}: {
  comps: Comp[];
  compAdjustmentResults: CompAdjustmentResult[];
}) {
  return (
    <div>
      <div className="font-bold pb-2">Comp Adjustments</div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Address</TableHead>
            <TableHead>Contract Date</TableHead>
            <TableHead className="text-right">Close Price</TableHead>
            <TableHead className="text-right">Calculated Effective Date Price</TableHead>
            <TableHead className="text-right">Adjustment</TableHead>
            <TableHead className="text-right">Percent Change</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {comps.map((comp) => {
            const result = compAdjustmentResults.find((r) => r.id === comp.id);
            if (!result) return null;

            return (
              <TableRow key={comp.id}>
                <TableCell>{result.address}</TableCell>
                <TableCell>{formatDate(result.saleDate.getTime())}</TableCell>
                <TableCell className="text-right">{formatPrice(result.salePrice)}</TableCell>
                <TableCell className="text-right">{formatPrice(result.adjustedValue)}</TableCell>
                {result.adjustmentAmount < 0 ? (
                  <TableCell className="text-right">
                    <span className="text-red-600 font-bold">
                      {formatPrice(result.adjustmentAmount)}
                    </span>
                  </TableCell>
                ) : (
                  <TableCell className="text-right">
                    <span className="text-green-600 font-bold">
                      +{formatPrice(result.adjustmentAmount)}
                    </span>
                  </TableCell>
                )}
                {result.percentChange < 0 ? (
                  <TableCell className="text-right">{result.percentChange.toFixed(1)}%</TableCell>
                ) : (
                  <TableCell className="text-right">{result.percentChange.toFixed(1)}%</TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function AppraiserNotes({ adjustment }: { adjustment: Adjustment }) {
  return (
    <div className="space-y-2">
      <div className="font-bold pb-2">Appraiser Notes</div>
      <div className="text-sm whitespace-pre-wrap">{adjustment.appraiser_notes}</div>
    </div>
  );
}

function TrendMethodology({ regression_model }: { regression_model: RegressionModel }) {
  return (
    <div>
      <div className="font-bold pb-2">Trend Methodology</div>
      <RegressionModelDetails model={regression_model} className="text-sm" />
    </div>
  );
}

export default function Component({
  user,
  adjustment,
  errors,
  notice,
  dataset_url,
  comps,
  outliers,
  regression_model,
}: Props) {
  const { data, isPending, error } = useFetch(dataset_url);

  const scatterplotData = data
    ? normalizeDataBasedOnVersion(data, outliers, comps).filter((d) => !d.outlier)
    : [];

  if (isPending) {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title={`Summary - ${adjustment.address}`} />
        <div className="py-4">
          <LoadingCard />
        </div>
      </AuthenticatedLayout>
    );
  }
  if (scatterplotData.length === 0) {
    return (
      <AuthenticatedLayout user={user} errors={errors} notice={notice}>
        <Head title={`Summary - ${adjustment.address}`} />
        <div className="py-4">
          <div className="pt-8 text-center">No data found</div>
        </div>
      </AuthenticatedLayout>
    );
  }

  const chartStuff =
    adjustment.effective_date && adjustment.address && scatterplotData.length > 0
      ? deriveChartStuff(scatterplotData, regression_model, {
          date: parseCalendarDate(adjustment.effective_date),
          name: adjustment.address || "",
        })
      : null;

  const compAdjustmentResults = chartStuff?.compAdjustmentResults;

  return (
    <AuthenticatedLayout user={user} errors={errors} notice={notice}>
      <Head title={`Summary - ${adjustment.address}`} />
      <div className="py-4">
        <div className="flex pt-4 mx-auto">
          <div className="flex-grow">
            <div className="w-full">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Market Condition Adjustments Summary</CardTitle>
                </CardHeader>

                <CardContent>
                  <PropertyHeader adjustment={adjustment} />

                  <div className="pt-8">
                    <div className="w-full space-y-4">
                      <div className="space-y-8">
                        <CompAdjustments
                          comps={comps}
                          compAdjustmentResults={compAdjustmentResults || []}
                        />

                        <div className="lg:flex lg:space-x-8 space-y-4 lg:space-y-0">
                          <div className="lg:w-1/3 space-y-8">
                            <AppraiserNotes adjustment={adjustment} />
                            {regression_model && (
                              <TrendMethodology regression_model={regression_model} />
                            )}
                          </div>
                          <div className="lg:w-2/3">
                            <div className="font-bold pb-2">Comparable Home Sales Trend</div>
                            <Card>
                              <CardContent className="pt-6">
                                {isPending && <div>Loading...</div>}
                                {error && <div>Error: {error}</div>}
                                {!isPending && !error && scatterplotData && (
                                  <ResponsiveContainer aspectRatio={16 / 6} maxHeight={600}>
                                    <ScatterplotSummary
                                      width={700}
                                      height={500}
                                      data={scatterplotData}
                                      regressionType={regression_model}
                                      onPointClick={() => {}}
                                      subject={
                                        adjustment.effective_date && adjustment.address
                                          ? {
                                              date: parseCalendarDate(adjustment.effective_date),
                                              name: adjustment.address || "",
                                            }
                                          : undefined
                                      }
                                      chartStuff={chartStuff}
                                    />
                                  </ResponsiveContainer>
                                )}
                              </CardContent>
                            </Card>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
